import type { AnnouncerAudioEngine } from '@src/audio/AnnouncerAudioEngine';
import type { CrowdBed } from '@src/audio/CrowdBed';
import { Sfx, SfxId } from '@src/audio/Sfx';
import { announcerGroup } from '@src/game/AnnouncerLines';
import type { SponsorCommercial } from '@src/game/SponsorCommercials';

export interface CommercialOptions {
  host: HTMLElement;
  sfx: Sfx;
  announcer: AnnouncerAudioEngine;
  crowd: CrowdBed;
  commercial: SponsorCommercial;
  onComplete: () => void;
}

/** Short bumper VO (~1.5–2s) — min hold only; waitVoiceThen honors VO completion. */
const OUT_BUMPER_MS = 1600;
const BACK_BUMPER_MS = 1400;
const BACK_BUMPER_SKIP_MS = 700;
/** Fallback when product VO metadata never loads. */
const DEFAULT_SPOT_MS = 12000;
/** Comfortable post-VO beat so punchline / disclaimer can land. */
const SPOT_TAIL_BUFFER_MS = 1200;
const SPOT_VOICE_START_DELAY_MS = 280;
const SKIP_ARM_MS = 450;
const VOICE_POLL_MS = 70;
/** Hard safety only — never cut a still-speaking bumper early on purpose. */
const BUMPER_VOICE_SAFETY_MS = 8000;

const OUT_HEADLINES = [
  'A QUICK WORD FROM OUR SPONSORS!',
  "DON'T TOUCH THAT ORB!",
  "WE'LL BE RIGHT BACK!",
] as const;

const BACK_HEADLINES = [
  'BACK TO THE CARNAGE!',
  "AND WE'RE BACK!",
  'WELCOME BACK TO THE MAYHEM!',
] as const;

const STATION_IDS = [
  'ABN  ·  CHANNEL VII',
  'WIZARD ARENA TELEVISION',
  'ARCANE BROADCAST NETWORK',
  'LIVE FROM THE GRAND ARENA',
] as const;

const TICKER_FRAGMENTS = [
  'CALL 1-800-555-CAST',
  'OPERATORS ARE STANDING BY VIA SCRYING ORB',
  'AS SEEN ON ARCANE TV',
  'SPECIAL TV OFFER WHILE PORTALS LAST',
  'ONLY 3 EASY PAYMENTS OF 19.99 COPPER PIECES',
  'NOT VALID IN NETHER REALMS',
  'ALLOW 4 TO 6 FULL MOONS FOR DELIVERY',
  'LIMITED STOCK  ·  HIGH CHAOS  ·  ZERO REFUNDS',
] as const;

const HOTLINE_TEXT = 'CALL 1-800-555-CAST  ·  OPERATORS ARE STANDING BY VIA SCRYING ORB';

type Phase = 'out' | 'spot' | 'back';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  parent?: HTMLElement,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  parent?.appendChild(node);
  return node;
}

function pick<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)]!;
}

function fallbackBumperLine(groupId: string): string {
  const group = announcerGroup(groupId);
  if (!group || group.variants.length === 0) return '';
  return pick(group.variants).caption;
}

function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function tickerCopy(c: SponsorCommercial): string {
  const bits = [
    c.hotlineText ?? HOTLINE_TEXT,
    ...(c.tickerLines ?? TICKER_FRAGMENTS),
    c.productName,
    c.slogan,
    c.offerSticker,
    c.disclaimer.replace(/\s+/g, ' ').trim(),
  ].filter((line) => line && line.trim().length > 0);
  const joined = bits.join('    ·    ').toUpperCase();
  return `${joined}    ·    ${joined}`;
}

export class CommercialCutscene {
  private readonly root: HTMLElement;
  private readonly bumper: HTMLElement;
  private readonly bumperEyebrow: HTMLElement;
  private readonly bumperHeadline: HTMLElement;
  private readonly bumperStation: HTMLElement;
  private readonly bumperLine: HTMLElement;
  private readonly spot: HTMLElement;
  private readonly progressBar: HTMLElement;
  private readonly timerText: HTMLElement;
  private readonly breakLabel: HTMLElement;
  private readonly sfx: Sfx;
  private readonly crowd: CrowdBed;
  private readonly announcer: AnnouncerAudioEngine;
  private readonly onComplete: () => void;
  private readonly prevCaption: AnnouncerAudioEngine['onCaption'];
  private readonly startTime: number;
  private readonly outHeadline: string;
  private readonly backHeadline: string;
  private readonly stationId: string;
  private voiceAudio: HTMLAudioElement | null = null;
  private spotDurationMs = DEFAULT_SPOT_MS;
  private spotFinishArmed = false;
  private phase: Phase = 'out';
  private phaseStartedAt: number;
  private animFrame = 0;
  private completed = false;
  private armed = false;
  private timers = new Set<number>();
  private cleanupListeners: () => void = () => {};

  constructor(opts: CommercialOptions) {
    this.sfx = opts.sfx;
    this.crowd = opts.crowd;
    this.announcer = opts.announcer;
    this.onComplete = opts.onComplete;
    this.prevCaption = opts.announcer.onCaption;
    opts.announcer.onCaption = (text) => {
      if (!this.completed && (this.phase === 'out' || this.phase === 'back')) {
        this.bumperLine.textContent = text;
      }
    };
    this.startTime = performance.now();
    this.phaseStartedAt = this.startTime;
    this.outHeadline = pick(OUT_HEADLINES);
    this.backHeadline = pick(BACK_HEADLINES);
    this.stationId = pick(STATION_IDS);

    const c = opts.commercial;

    const root = el('div', `commercial-overlay${c.tone === 'calm' ? ' tone-calm' : ''}`, opts.host);
    this.root = root;
    root.style.setProperty('--accent', c.accentColor);

    el('div', 'commercial-vignette', root);

    const mast = el('header', 'commercial-masthead', root);
    const mastLeft = el('div', 'commercial-mast-left', mast);
    const live = el('span', 'commercial-live', mastLeft);
    el('i', 'commercial-live-dot', live);
    el('span', 'commercial-live-text', live, 'LIVE');
    el('span', 'commercial-network', mastLeft, 'ARCANE BROADCAST NETWORK');

    const mastRight = el('div', 'commercial-mast-right', mast);
    this.breakLabel = el('span', 'commercial-break-label', mastRight, 'STATION BREAK');
    this.timerText = el('span', 'commercial-clock', mastRight, '0:00');

    const skipPrompt = el('button', 'commercial-skip', mastRight);
    skipPrompt.type = 'button';
    skipPrompt.textContent = 'SKIP';

    const stage = el('div', 'commercial-stage', root);

    this.bumper = el('div', 'commercial-bumper', stage);
    const bumperCard = el('div', 'commercial-bumper-card', this.bumper);
    this.bumperEyebrow = el('div', 'commercial-bumper-eyebrow', bumperCard, 'PLEASE STAND BY');
    el('div', 'commercial-bumper-mark', bumperCard, 'ABN');
    this.bumperHeadline = el('h1', 'commercial-bumper-headline', bumperCard, this.outHeadline);
    this.bumperStation = el('div', 'commercial-bumper-station', bumperCard, this.stationId);
    this.bumperLine = el('p', 'commercial-bumper-line', bumperCard, '');

    this.spot = el('div', 'commercial-spot', stage);
    this.spot.hidden = true;

    const media = el('div', 'commercial-media', this.spot);

    const video = document.createElement('video');
    video.className = 'commercial-video';
    video.src = `/assets/commercials/${c.id}.mp4`;
    video.autoplay = true;
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    media.appendChild(video);

    const img = document.createElement('img');
    img.className = 'commercial-img';
    img.src = `/assets/commercials/${c.id}.jpg`;
    img.alt = c.productName;
    img.decoding = 'async';
    media.appendChild(img);

    video.addEventListener('canplay', () => {
      video.classList.add('ready');
      video.play().catch(() => {});
    });
    video.addEventListener('error', () => {
      video.remove();
    });
    img.addEventListener('error', () => {
      img.remove();
    });

    const bug = el('div', 'commercial-bug', this.spot);
    el('span', 'commercial-bug-mark', bug, 'ABN');

    const l3 = el('div', 'commercial-l3', this.spot);
    el('div', 'commercial-l3-kicker', l3, `${c.badge}  ·  ${c.sponsorName}`);
    el('h1', 'commercial-l3-title', l3, c.productName);
    el('p', 'commercial-l3-slogan', l3, c.slogan);

    const ticker = el('footer', 'commercial-ticker', root);
    el('span', 'commercial-ticker-bug', ticker, 'ABN');
    const tickerMask = el('div', 'commercial-ticker-mask', ticker);
    const tickerTrack = el('div', 'commercial-ticker-track', tickerMask);
    el('span', 'commercial-ticker-text', tickerTrack, tickerCopy(c));

    const progress = el('div', 'commercial-progress', root);
    this.progressBar = el('i', 'commercial-progress-fill', progress);
    this.progressBar.style.backgroundColor = c.accentColor;

    skipPrompt.addEventListener('click', (e) => {
      e.stopPropagation();
      this.requestSkip();
    });
    root.addEventListener('click', () => {
      if (this.armed) this.requestSkip();
    });

    this.bindInputs();
    this.preloadVoice(c.id);
    this.enterOutBumper();
    this.delay(SKIP_ARM_MS, () => {
      this.armed = true;
    });
    this.tick();
  }

  private totalDurationMs(): number {
    return OUT_BUMPER_MS + SPOT_VOICE_START_DELAY_MS + this.spotDurationMs + BACK_BUMPER_MS;
  }

  private delay(ms: number, fn: () => void): void {
    const id = window.setTimeout(() => {
      this.timers.delete(id);
      if (!this.completed) fn();
    }, ms);
    this.timers.add(id);
  }

  private clearTimers(): void {
    for (const id of this.timers) window.clearTimeout(id);
    this.timers.clear();
  }

  private preloadVoice(id: string): void {
    try {
      const voice = new Audio(`/assets/commercials/${id}.mp3`);
      voice.preload = 'auto';
      voice.volume = 0.95;
      this.voiceAudio = voice;
      voice.addEventListener('loadedmetadata', () => {
        if (voice.duration && isFinite(voice.duration) && voice.duration > 0) {
          this.spotDurationMs = voice.duration * 1000 + SPOT_TAIL_BUFFER_MS;
        }
      });
      voice.addEventListener('ended', () => {
        this.armSpotFinish();
      });
    } catch {
      // Audio element creation error fallback
    }
  }

  /** Auto-finish the product spot after the tail buffer. Manual skip bypasses this. */
  private armSpotFinish(): void {
    if (this.spotFinishArmed || this.completed || this.phase !== 'spot') return;
    this.spotFinishArmed = true;
    this.delay(SPOT_TAIL_BUFFER_MS, () => {
      if (!this.completed && this.phase === 'spot') this.enterBackBumper(false);
    });
  }

  /** If VO is still playing when a safety timer fires, keep waiting until it ends. */
  private waitForProductVoiceThenFinish(): void {
    const poll = (): void => {
      if (this.completed || this.phase !== 'spot' || this.spotFinishArmed) return;
      const voice = this.voiceAudio;
      if (voice && !voice.paused && !voice.ended) {
        this.delay(VOICE_POLL_MS, poll);
        return;
      }
      this.armSpotFinish();
    };
    poll();
  }

  private enterOutBumper(): void {
    this.phase = 'out';
    this.phaseStartedAt = performance.now();
    this.breakLabel.textContent = 'STATION BREAK';
    this.bumper.hidden = false;
    this.bumper.classList.remove('returning');
    this.spot.hidden = true;
    this.root.classList.remove('spot-live');
    this.bumperEyebrow.textContent = 'PLEASE STAND BY';
    this.bumperHeadline.textContent = this.outHeadline;
    this.bumperStation.textContent = this.stationId;
    this.bumperLine.textContent = fallbackBumperLine('commercial_out');

    this.sfx.play(SfxId.OnAir, 0.85);
    this.delay(180, () => {
      if (this.completed || this.phase !== 'out') return;
      this.sfx.play(SfxId.CommercialJingle, 1.0);
    });
    this.delay(280, () => {
      if (this.completed || this.phase !== 'out') return;
      this.announcer.say('commercial_out');
    });

    this.waitVoiceThen(OUT_BUMPER_MS, () => {
      if (!this.completed && this.phase === 'out') this.enterSpot();
    });
  }

  private enterSpot(): void {
    this.phase = 'spot';
    this.phaseStartedAt = performance.now();
    this.spotFinishArmed = false;
    this.breakLabel.textContent = 'PAID PROGRAMMING';
    this.bumper.hidden = true;
    this.spot.hidden = false;
    this.spot.classList.add('revealed');
    this.root.classList.add('spot-live');

    this.crowd.sting('applause', 0.42, 0, true);
    this.delay(120, () => {
      if (this.completed || this.phase !== 'spot') return;
      this.sfx.play(SfxId.OrchestraHit, 0.45);
    });
    this.delay(SPOT_VOICE_START_DELAY_MS, () => {
      if (this.completed || this.phase !== 'spot' || !this.voiceAudio) return;
      this.voiceAudio.currentTime = 0;
      this.voiceAudio.play().catch(() => {
        // Playback blocked — fall through to safety finish below.
      });
    });

    this.delay(SPOT_VOICE_START_DELAY_MS + this.spotDurationMs, () => {
      if (this.completed || this.phase !== 'spot' || this.spotFinishArmed) return;
      this.waitForProductVoiceThenFinish();
    });
  }

  private enterBackBumper(fromSkip: boolean): void {
    if (this.phase === 'back') return;
    this.phase = 'back';
    this.phaseStartedAt = performance.now();
    this.breakLabel.textContent = 'RETURNING LIVE';
    this.stopProductVoice();
    this.spot.hidden = true;
    this.root.classList.remove('spot-live');
    this.bumper.hidden = false;
    this.bumper.classList.add('returning');
    this.bumperEyebrow.textContent = 'WE NOW RETURN YOU TO';
    this.bumperHeadline.textContent = this.backHeadline;
    this.bumperStation.textContent = this.stationId;
    this.bumperLine.textContent = fallbackBumperLine('commercial_back');

    this.sfx.play(SfxId.OnAir, 0.75);
    this.delay(100, () => {
      if (this.completed || this.phase !== 'back') return;
      this.sfx.play(SfxId.Prize, 0.55);
      this.crowd.sting('cheer', 0.55, 0.08, true);
      this.announcer.interrupt(true);
      this.announcer.say('commercial_back');
    });

    const hold = fromSkip ? BACK_BUMPER_SKIP_MS : BACK_BUMPER_MS;
    this.waitVoiceThen(hold, () => {
      if (!this.completed && this.phase === 'back') this.finish();
    });
  }

  private waitVoiceThen(minMs: number, fn: () => void): void {
    const started = performance.now();
    const poll = (): void => {
      if (this.completed) return;
      const elapsed = performance.now() - started;
      const voiceDone = !this.announcer.isSpeaking();
      if (elapsed >= minMs && (voiceDone || elapsed >= BUMPER_VOICE_SAFETY_MS)) {
        fn();
        return;
      }
      this.delay(VOICE_POLL_MS, poll);
    };
    this.delay(VOICE_POLL_MS, poll);
  }

  private requestSkip(): void {
    if (!this.armed || this.completed) return;
    if (this.phase === 'back') {
      this.finish();
      return;
    }
    this.clearTimers();
    this.announcer.interrupt(true);
    this.enterBackBumper(true);
  }

  private tick = (): void => {
    if (this.completed) return;
    const total = this.totalDurationMs();
    const elapsed = performance.now() - this.startTime;
    const progress = Math.min(1, elapsed / total);
    this.progressBar.style.transform = `scaleX(${progress})`;

    let phaseRemain = 0;
    if (this.phase === 'out') {
      phaseRemain = Math.max(0, OUT_BUMPER_MS - (performance.now() - this.phaseStartedAt));
    } else if (this.phase === 'spot') {
      phaseRemain = Math.max(
        0,
        SPOT_VOICE_START_DELAY_MS + this.spotDurationMs - (performance.now() - this.phaseStartedAt),
      );
    } else {
      phaseRemain = Math.max(0, BACK_BUMPER_MS - (performance.now() - this.phaseStartedAt));
    }
    this.timerText.textContent = formatClock(phaseRemain);

    this.animFrame = requestAnimationFrame(this.tick);
  };

  private bindInputs(): void {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!this.armed) return;
      if (e.code === 'Space' || e.code === 'Enter' || e.code === 'Escape' || e.code === 'KeyP') {
        e.preventDefault();
        e.stopPropagation();
        this.requestSkip();
      }
    };

    let pollPad = 0;
    const checkGamepad = () => {
      if (this.completed) return;
      const pads = navigator.getGamepads?.() ?? [];
      for (let i = 0; i < pads.length; i++) {
        const p = pads[i];
        if (!p) continue;
        if (p.buttons[0]?.pressed || p.buttons[1]?.pressed || p.buttons[9]?.pressed) {
          if (this.armed) {
            this.requestSkip();
            return;
          }
        }
      }
      pollPad = requestAnimationFrame(checkGamepad);
    };
    pollPad = requestAnimationFrame(checkGamepad);

    window.addEventListener('keydown', onKeyDown, { capture: true });

    this.cleanupListeners = () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true });
      cancelAnimationFrame(pollPad);
    };
  }

  private stopProductVoice(): void {
    if (!this.voiceAudio) return;
    try {
      this.voiceAudio.pause();
      this.voiceAudio.currentTime = 0;
    } catch {
      // audio cleanup
    }
  }

  public finish(): void {
    if (this.completed) return;
    this.completed = true;
    cancelAnimationFrame(this.animFrame);
    this.clearTimers();
    this.cleanupListeners();
    this.stopProductVoice();
    this.voiceAudio = null;
    this.announcer.onCaption = this.prevCaption;

    this.root.classList.add('leaving');
    window.setTimeout(() => {
      this.root.remove();
      this.onComplete();
    }, 280);
  }
}
